import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingBlock } from "../../components/common/LoadingBlock";
import { StatusBanner } from "../../components/common/StatusBanner";
import { KioskLayout } from "../../components/kiosk/KioskLayout";
import {
  SignaturePad,
  type SignaturePadHandle,
} from "../../components/kiosk/SignaturePad";
import { useIdleTimeout } from "../../hooks/useIdleTimeout";
import { useSessionStorageState } from "../../hooks/useSessionStorageState";
import {
  kioskCheckout,
  kioskListOpenVisitsByCompany,
  kioskSearchOpenVisitCompanies,
} from "../../services/rpc";
import { deleteSignature, uploadSignature } from "../../services/storage";
import type {
  CheckoutDraft,
  CheckoutFieldErrors,
  CompanyOpenVisitOption,
  OpenVisitCompanyOption,
} from "../../types/domain";
import { toErrorMessage } from "../../utils/errors";
import { focusNextFieldOnEnter, scrollToFirstError, scrollToStep } from "../../utils/kioskForm";
import { validateCheckoutSelection } from "../../utils/validation";

const INITIAL_DRAFT: CheckoutDraft = {
  companySearch: "",
  selectedCompany: "",
  selectedVisitId: "",
  signatureDataUrl: "",
};

export function CheckoutPage() {
  const navigate = useNavigate();
  const signatureRef = useRef<SignaturePadHandle | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const visitorStepRef = useRef<HTMLElement | null>(null);
  const signatureStepRef = useRef<HTMLElement | null>(null);
  const companySearchRequestIdRef = useRef(0);
  const companyVisitsRequestIdRef = useRef(0);
  const { value: draft, setValue: setDraft, clear } = useSessionStorageState(
    "kiosk-checkout-draft",
    INITIAL_DRAFT,
  );
  const [companyOptions, setCompanyOptions] = useState<OpenVisitCompanyOption[]>(
    [],
  );
  const [companyVisits, setCompanyVisits] = useState<CompanyOpenVisitOption[]>([]);
  const [fieldErrors, setFieldErrors] = useState<CheckoutFieldErrors>({});
  const [companySearchErrorMessage, setCompanySearchErrorMessage] = useState<
    string | null
  >(null);
  const [companyVisitsErrorMessage, setCompanyVisitsErrorMessage] = useState<
    string | null
  >(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null,
  );
  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);
  const [isLoadingCompanyVisits, setIsLoadingCompanyVisits] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useIdleTimeout(120000, () => {
    clear();
    navigate("/", { replace: true });
  });

  function updateDraft(nextValues: Partial<CheckoutDraft>) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      ...nextValues,
    }));
  }

  function resetCompanySelection() {
    companySearchRequestIdRef.current += 1;
    companyVisitsRequestIdRef.current += 1;
    setIsSearchingCompanies(false);
    setIsLoadingCompanyVisits(false);
    setCompanyOptions([]);
    setCompanyVisits([]);
    setFieldErrors({});
    setCompanySearchErrorMessage(null);
    setCompanyVisitsErrorMessage(null);
    setSubmitErrorMessage(null);
    updateDraft({
      companySearch: "",
      selectedCompany: "",
      selectedVisitId: "",
      signatureDataUrl: "",
    });
  }

  function handleCompanySelect(company: string) {
    // Chiude la tastiera a schermo e porta in vista l'elenco dei visitatori.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    scrollToStep(visitorStepRef.current);
    companySearchRequestIdRef.current += 1;
    setIsSearchingCompanies(false);
    setCompanyOptions([]);
    setFieldErrors({});
    setCompanySearchErrorMessage(null);
    setCompanyVisitsErrorMessage(null);
    setSubmitErrorMessage(null);
    updateDraft({
      companySearch: company,
      selectedCompany: company,
      selectedVisitId: "",
      signatureDataUrl: "",
    });
  }

  useEffect(() => {
    if (draft.selectedCompany) {
      return undefined;
    }

    const companyQuery = draft.companySearch.trim();
    companySearchRequestIdRef.current += 1;
    const requestId = companySearchRequestIdRef.current;

    if (companyQuery.length < 2) {
      setIsSearchingCompanies(false);
      setCompanyOptions([]);
      setCompanySearchErrorMessage(null);
      return undefined;
    }

    setIsSearchingCompanies(true);
    setCompanySearchErrorMessage(null);

    const timeoutId = window.setTimeout(() => {
      kioskSearchOpenVisitCompanies(companyQuery)
        .then((companies) => {
          if (companySearchRequestIdRef.current !== requestId) {
            return;
          }

          setCompanyOptions(companies);
        })
        .catch((error) => {
          if (companySearchRequestIdRef.current !== requestId) {
            return;
          }

          setCompanyOptions([]);
          setCompanySearchErrorMessage(
            toErrorMessage(
              error,
              "Impossibile cercare le aziende con visite aperte.",
            ),
          );
        })
        .finally(() => {
          if (companySearchRequestIdRef.current === requestId) {
            setIsSearchingCompanies(false);
          }
        });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [draft.companySearch, draft.selectedCompany]);

  useEffect(() => {
    const selectedCompany = draft.selectedCompany.trim();
    companyVisitsRequestIdRef.current += 1;
    const requestId = companyVisitsRequestIdRef.current;

    if (!selectedCompany) {
      setIsLoadingCompanyVisits(false);
      setCompanyVisits([]);
      setCompanyVisitsErrorMessage(null);
      return;
    }

    setIsLoadingCompanyVisits(true);
    setCompanyVisits([]);
    setCompanyVisitsErrorMessage(null);

    kioskListOpenVisitsByCompany(selectedCompany)
      .then((visits) => {
        if (companyVisitsRequestIdRef.current !== requestId) {
          return;
        }

        setCompanyVisits(visits);
        setDraft((currentDraft) => {
          if (currentDraft.selectedCompany !== selectedCompany) {
            return currentDraft;
          }

          if (
            currentDraft.selectedVisitId &&
            !visits.some((visit) => visit.visitId === currentDraft.selectedVisitId)
          ) {
            return {
              ...currentDraft,
              selectedVisitId: "",
              signatureDataUrl: "",
            };
          }

          return currentDraft;
        });
      })
      .catch((error) => {
        if (companyVisitsRequestIdRef.current !== requestId) {
          return;
        }

        setCompanyVisits([]);
        setCompanyVisitsErrorMessage(
          toErrorMessage(
            error,
            "Impossibile recuperare i visitatori dell'azienda selezionata.",
          ),
        );
      })
      .finally(() => {
        if (companyVisitsRequestIdRef.current === requestId) {
          setIsLoadingCompanyVisits(false);
        }
      });
  }, [draft.selectedCompany, setDraft]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const nextErrors = validateCheckoutSelection(
      draft.selectedVisitId,
      draft.signatureDataUrl,
    );
    setFieldErrors(nextErrors);
    setSubmitErrorMessage(null);

    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstError(formRef.current);
      return;
    }

    setIsSubmitting(true);
    let signaturePath = "";

    try {
      const signatureBlob = await signatureRef.current?.toBlob();
      if (!signatureBlob) {
        throw new Error("La firma non è disponibile.");
      }

      signaturePath = await uploadSignature("checkout", signatureBlob);
      await kioskCheckout(draft.selectedVisitId, signaturePath);
      clear();
      navigate("/success/checkout", { replace: true });
    } catch (error) {
      if (signaturePath) {
        deleteSignature(signaturePath).catch(() => undefined);
      }

      setSubmitErrorMessage(
        toErrorMessage(error, "Il check-out non è andato a buon fine. Riprova."),
      );
      scrollToFirstError(formRef.current);
    } finally {
      setIsSubmitting(false);
    }
  }

  const companyQuery = draft.companySearch.trim();
  const shouldShowCompanyEmptyState =
    !draft.selectedCompany &&
    companyQuery.length >= 2 &&
    !isSearchingCompanies &&
    !companySearchErrorMessage &&
    companyOptions.length === 0;

  return (
    <KioskLayout
      title="Registrazione uscita"
      subtitle="Prima seleziona l'azienda, poi scegli il visitatore corretto e acquisisci la firma finale."
    >
      <form
        ref={formRef}
        className="kiosk-form kiosk-form--stacked"
        onSubmit={handleSubmit}
        onKeyDown={focusNextFieldOnEnter}
        noValidate
      >
        <section className="selection-panel">
          <div className="selection-panel__header">
            <div>
              <h2>1. Cerca azienda</h2>
              <p>
                Nessun nominativo viene mostrato all'apertura: i visitatori si
                caricano solo dopo la scelta dell'azienda.
              </p>
            </div>
            {draft.selectedCompany ? (
              <button
                className="ghost-button"
                type="button"
                onClick={resetCompanySelection}
              >
                Cambia azienda
              </button>
            ) : null}
          </div>

          {!draft.selectedCompany ? (
            <>
              <input
                className="search-input"
                type="search"
                placeholder="Cerca azienda"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="search"
                aria-label="Cerca azienda"
                value={draft.companySearch}
                onChange={(event) => {
                  setCompanySearchErrorMessage(null);
                  setSubmitErrorMessage(null);
                  updateDraft({ companySearch: event.target.value });
                }}
              />

              {companySearchErrorMessage ? (
                <StatusBanner tone="error" message={companySearchErrorMessage} />
              ) : null}

              {companyQuery.length < 2 ? (
                <div className="empty-state">
                  Digita almeno 2 caratteri per cercare un'azienda con visite
                  aperte.
                </div>
              ) : null}

              {isSearchingCompanies ? (
                <LoadingBlock label="Ricerca aziende con visite aperte..." />
              ) : null}

              {shouldShowCompanyEmptyState ? (
                <div className="empty-state">
                  Nessuna azienda con visite aperte trovata per questa ricerca.
                </div>
              ) : null}

              {!isSearchingCompanies && companyOptions.length > 0 ? (
                <div className="visit-choice-grid">
                  {companyOptions.map((companyOption) => (
                    <button
                      key={companyOption.company}
                      className="visit-choice"
                      type="button"
                      onClick={() => handleCompanySelect(companyOption.company)}
                    >
                      <strong>{companyOption.company}</strong>
                      <small>Mostra solo i visitatori di questa azienda</small>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div className="visit-choice-grid">
              <div className="visit-choice visit-choice--active">
                <strong>{draft.selectedCompany}</strong>
                <small>Azienda selezionata per il check-out</small>
              </div>
            </div>
          )}
        </section>

        <section className="selection-panel" ref={visitorStepRef}>
          <div className="selection-panel__header">
            <div>
              <h2>2. Seleziona il visitatore</h2>
              <p>
                Dopo la scelta dell'azienda mostriamo solo i nominativi
                strettamente necessari a completare l'uscita.
              </p>
            </div>
          </div>

          {!draft.selectedCompany ? (
            <div className="empty-state">
              Seleziona prima un'azienda per vedere i visitatori disponibili.
            </div>
          ) : (
            <>
              {fieldErrors.selectedVisitId ? (
                <StatusBanner tone="error" message={fieldErrors.selectedVisitId} />
              ) : null}

              {companyVisitsErrorMessage ? (
                <StatusBanner tone="error" message={companyVisitsErrorMessage} />
              ) : null}

              {isLoadingCompanyVisits ? (
                <LoadingBlock label="Caricamento visitatori dell'azienda..." />
              ) : companyVisits.length === 0 && !companyVisitsErrorMessage ? (
                <div className="empty-state">
                  Nessuna visita aperta trovata per l'azienda selezionata.
                </div>
              ) : (
                <div className="visit-choice-grid">
                  {companyVisits.map((visit) => (
                    <button
                      key={visit.visitId}
                      className={`visit-choice ${
                        draft.selectedVisitId === visit.visitId
                          ? "visit-choice--active"
                          : ""
                      }`}
                      type="button"
                      onClick={() => {
                        setSubmitErrorMessage(null);
                        setFieldErrors((currentErrors) => ({
                          ...currentErrors,
                          selectedVisitId: undefined,
                        }));
                        updateDraft({
                          selectedVisitId: visit.visitId,
                        });
                        scrollToStep(signatureStepRef.current);
                      }}
                    >
                      <strong>{visit.displayName}</strong>
                      <small>Entrata: {visit.checkinLocal}</small>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <section className="selection-panel" ref={signatureStepRef}>
          <div className="selection-panel__header">
            <div>
              <h2>3. Firma di uscita</h2>
            </div>
          </div>
          <SignaturePad
            ref={signatureRef}
            label="Firma visitatore"
            valueDataUrl={draft.signatureDataUrl}
            error={fieldErrors.signature}
            onChange={(signatureDataUrl) => updateDraft({ signatureDataUrl })}
          />
        </section>

        {submitErrorMessage ? (
          <StatusBanner tone="error" message={submitErrorMessage} />
        ) : null}
        <div className="toolbar-actions toolbar-actions--end">
          <button
            className="ghost-button"
            type="button"
            onClick={() => {
              clear();
              navigate("/");
            }}
          >
            Annulla
          </button>
          <button className="primary-button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Registrazione..." : "Conferma uscita"}
          </button>
        </div>
      </form>
    </KioskLayout>
  );
}
